<!-- Hero -->
<section
  class="relative flex min-h-[calc(100svh-4.25rem)] items-center overflow-hidden bg-ink sm:min-h-0 sm:py-24"
>
  <div class="pointer-events-none absolute inset-0" aria-hidden="true">
    <img
      src="assets/hero/saddle-close.webp"
      alt=""
      fetchpriority="high"
      class="absolute inset-0 h-full w-full scale-105 object-cover opacity-40"
    />
    <div class="absolute inset-0 bg-gradient-to-t from-ink via-ink/85 to-ink/55"></div>
    <div class="hero-grain absolute inset-0 opacity-[0.05] mix-blend-overlay"></div>
  </div>

  <div class="relative mx-auto w-full max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
    <div class="mx-auto max-w-2xl text-center">
      <p class="text-sm font-medium uppercase tracking-wide text-gold">Shipping calculator</p>
      <h1 class="mt-4 font-display text-4xl font-semibold text-cream sm:text-5xl">
        Are you shipping a saddle? Get a free estimate in seconds.
      </h1>
      <p class="mt-5 text-lg text-cream/75">
        Enter a "from" and "to" postcode and we'll estimate what it costs to box and post a saddle
        anywhere in Australia — a live Australia Post quote, so it's approximate, not a fixed price.
      </p>
    </div>

    <!-- Calculator card -->
    <div
      class="relative mx-auto mt-12 max-w-2xl rounded-3xl border border-line bg-white p-6 sm:p-8"
    >
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <app-postcode-autocomplete
          #fromInput
          inputId="from-postcode"
          label="From postcode"
          placeholder="e.g. Bondi or 2026"
        />
        <app-postcode-autocomplete
          #toInput
          inputId="to-postcode"
          label="To postcode"
          placeholder="e.g. Fremantle or 6160"
        />
      </div>

      <div class="mt-5">
        <label class="block text-sm font-medium text-ink">Saddle type</label>
        <div class="mt-1.5 flex flex-col gap-2 sm:flex-row sm:gap-3">
          <label
            class="flex flex-1 items-center gap-2.5 rounded-xl border border-line px-3.5 py-2.5 text-sm text-ink transition has-[:checked]:border-forest has-[:checked]:bg-forest/5"
          >
            <input
              type="radio"
              name="saddleType"
              [checked]="saddleType() === 'OTHER'"
              (change)="saddleType.set('OTHER')"
              class="h-4 w-4 border-line text-forest"
            />
            English / General purpose
          </label>
          <label
            class="flex flex-1 items-center gap-2.5 rounded-xl border border-line px-3.5 py-2.5 text-sm text-ink transition has-[:checked]:border-forest has-[:checked]:bg-forest/5"
          >
            <input
              type="radio"
              name="saddleType"
              [checked]="saddleType() === 'WESTERN'"
              (change)="saddleType.set('WESTERN')"
              class="h-4 w-4 border-line text-forest"
            />
            Western
          </label>
        </div>
        <p class="mt-1.5 text-xs text-ink/50">
          Western saddles need a meaningfully bigger, heavier box — this changes the estimate.
        </p>
      </div>

      <div class="mt-5">
        <label class="block text-sm font-medium text-ink" for="saddle-value"
          >Approximate saddle value (AUD)</label
        >
        <input
          id="saddle-value"
          type="number"
          min="0"
          step="50"
          [value]="valueAud()"
          (input)="valueAud.set($any($event.target).valueAsNumber || 0)"
          class="mt-1.5 w-full rounded-lg border border-line px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30"
        />
        <p class="mt-1.5 text-xs text-ink/50">
          Used to declare cover on the parcel — Signature on Delivery and Extra Cover are included
          in every estimate.
        </p>
      </div>

      @if (errorMessage()) {
        <p class="mt-4 text-sm text-rose">{{ errorMessage() }}</p>
      }

      <button
        type="button"
        (click)="calculate()"
        [disabled]="submitting()"
        class="mt-6 flex w-full items-center justify-center gap-2.5 rounded-full bg-forest px-6 py-3.5 text-sm font-medium text-cream transition hover:bg-forestdark disabled:opacity-60 sm:w-auto"
      >
        @if (submitting()) {
          <span
            class="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-cream/40 border-t-cream"
            aria-hidden="true"
          ></span>
        }
        {{ submitting() ? 'Calculating…' : 'Calculate shipping cost' }}
      </button>

      @if (submitting()) {
        <div class="mt-6 flex items-center gap-4 rounded-2xl bg-creamdark/50 p-5">
          <span
            class="h-8 w-8 shrink-0 animate-spin rounded-full border-2 border-forest/25 border-t-forest"
            aria-hidden="true"
          ></span>
          <p class="text-sm text-ink/70">{{ loadingMessage() }}</p>
        </div>
      } @else if (result(); as result) {
        <div class="mt-6 rounded-2xl bg-creamdark/50 p-5">
          <p class="text-xs font-medium uppercase tracking-wide text-tandark">
            Approximate shipping cost
          </p>
          <p class="mt-1 font-display text-3xl font-semibold text-ink">
            {{ formatPriceCents(result.deliveryFeeCents, 'AUD') }}
          </p>
          <p class="mt-2 text-sm text-ink/60">
            Includes tracking, Signature on Delivery, and Extra Cover up to
            {{ formatPriceCents(valueAud() * 100, 'AUD') }} — a live Australia Post Regular Parcel
            quote, not a fixed price. Actual cost on the day may vary.
          </p>
        </div>

        <div class="mt-4 rounded-2xl border border-line bg-white p-5 text-center">
          <p class="font-semibold text-ink">Buy or sell saddles Australia-wide</p>
          <p class="mt-1 text-sm text-ink/60">
            Verified sellers, protected payments, and delivery pricing that's never a surprise.
          </p>
          <div class="mt-4 flex flex-wrap items-center justify-center gap-3">
            @if (authService.isAuthenticated()) {
              <a
                routerLink="/shop"
                class="rounded-full bg-forest px-5 py-2.5 text-sm font-medium text-cream transition hover:bg-forestdark"
              >
                Shop saddles
              </a>
              <a
                routerLink="/account/listings/new"
                class="rounded-full border border-line px-5 py-2.5 text-sm font-medium text-ink transition hover:border-tan"
              >
                Sell a saddle
              </a>
            } @else {
              <a
                routerLink="/signup"
                class="rounded-full bg-forest px-5 py-2.5 text-sm font-medium text-cream transition hover:bg-forestdark"
              >
                Join Galopa
              </a>
              <a
                routerLink="/login"
                class="rounded-full border border-line px-5 py-2.5 text-sm font-medium text-ink transition hover:border-tan"
              >
                Log in
              </a>
            }
          </div>
        </div>
      }
    </div>
  </div>
</section>

<!-- Trust strip -->
<section class="border-t border-cream/10 bg-forest text-cream">
  <div
    class="mx-auto grid max-w-5xl grid-cols-1 gap-x-4 gap-y-8 px-4 py-10 sm:grid-cols-3 sm:gap-6 sm:px-6 sm:py-8 lg:px-8"
  >
    <div
      class="flex flex-col items-center gap-2.5 text-center sm:flex-row sm:items-start sm:gap-3 sm:text-left"
    >
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream/10 text-cream sm:h-11 sm:w-11"
      >
        <app-icon name="truck" svgClass="h-[18px] w-[18px]" [strokeWidth]="2" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-semibold leading-snug text-cream text-balance">
          Live Australia Post rates
        </p>
        <p class="mt-0.5 text-xs leading-snug text-cream/65">Same quote engine as checkout</p>
      </div>
    </div>
    <div
      class="flex flex-col items-center gap-2.5 text-center sm:flex-row sm:items-start sm:gap-3 sm:text-left"
    >
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream/10 text-cream sm:h-11 sm:w-11"
      >
        <app-icon name="alert" svgClass="h-[18px] w-[18px]" [strokeWidth]="2" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-semibold leading-snug text-cream text-balance">
          Approximate, not fixed
        </p>
        <p class="mt-0.5 text-xs leading-snug text-cream/65">Actual cost may vary on the day</p>
      </div>
    </div>
    <div
      class="flex flex-col items-center gap-2.5 text-center sm:flex-row sm:items-start sm:gap-3 sm:text-left"
    >
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream/10 text-cream sm:h-11 sm:w-11"
      >
        <app-icon name="mapPin" svgClass="h-[18px] w-[18px]" [strokeWidth]="2" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-semibold leading-snug text-cream text-balance">Australia-wide</p>
        <p class="mt-0.5 text-xs leading-snug text-cream/65">Any postcode to any postcode</p>
      </div>
    </div>
  </div>
</section>

<!-- How it's calculated -->
<section class="mx-auto max-w-4xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8" appReveal>
  <p class="mb-3 text-center text-sm font-medium uppercase tracking-wide text-tandark">
    How it's calculated
  </p>
  <h2 class="text-center font-display text-3xl font-semibold text-ink sm:text-4xl">
    A real Australia Post quote, boxed for a saddle
  </h2>

  <div class="mt-14 grid grid-cols-1 gap-10 sm:grid-cols-3">
    <div class="flex flex-col items-center text-center">
      <span
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-forest font-display font-semibold text-cream"
        >1</span
      >
      <p class="mb-1 mt-4 font-semibold text-ink">A saddle-sized box</p>
      <p class="text-sm text-ink/70">
        English/general purpose and Western saddles use different placeholder box dimensions and
        weights — Western needs a meaningfully bigger, heavier box.
      </p>
    </div>
    <div class="flex flex-col items-center text-center">
      <span
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-forest font-display font-semibold text-cream"
        >2</span
      >
      <p class="mb-1 mt-4 font-semibold text-ink">Signature + cover included</p>
      <p class="text-sm text-ink/70">
        Every estimate includes Signature on Delivery and Extra Cover declared up to the saddle's
        value — the same protections a real Galopa delivery sale includes.
      </p>
    </div>
    <div class="flex flex-col items-center text-center">
      <span
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-forest font-display font-semibold text-cream"
        >3</span
      >
      <p class="mb-1 mt-4 font-semibold text-ink">Live, not cached</p>
      <p class="text-sm text-ink/70">
        Each calculation calls Australia Post's own rates in real time — the same integration
        Galopa's checkout uses, so the number here is honest.
      </p>
    </div>
  </div>
</section>

<!-- FAQ -->
<section class="bg-creamdark/50 px-4 py-20 sm:px-6 sm:py-24 lg:px-8" appReveal>
  <div class="mx-auto max-w-3xl">
    <p class="mb-3 text-center text-sm font-medium uppercase tracking-wide text-tandark">
      Questions
    </p>
    <h2 class="text-center font-display text-3xl font-semibold text-ink sm:text-4xl">
      Before you box it up
    </h2>

    <dl class="mt-12 space-y-8">
      <div>
        <dt class="font-semibold text-ink">Is this exactly what I'll pay?</dt>
        <dd class="mt-2 text-ink/70">
          No — it's an approximate, real-time Australia Post quote based on placeholder box
          dimensions for a saddle. Your actual box, packing, and the exact rate on the day may
          differ slightly.
        </dd>
      </div>
      <div>
        <dt class="font-semibold text-ink">Does Galopa ship the saddle for me?</dt>
        <dd class="mt-2 text-ink/70">
          No — you still box and post it yourself (or arrange a courier). This tool is just here to
          help you plan and price a sale before you commit, whether that's on Galopa or anywhere
          else.
        </dd>
      </div>
      <div>
        <dt class="font-semibold text-ink">I'm shipping to someone I don't know — is that safe?</dt>
        <dd class="mt-2 text-ink/70">
          If you've struck a deal with a stranger (Facebook Marketplace, a noticeboard, etc.),
          Galopa's
          <a routerLink="/secure-trade" class="font-medium text-forest hover:underline"
            >Secure Trade</a
          >
          service holds the buyer's payment until you both confirm the saddle arrived as described —
          so nobody has to ship first on trust alone.
        </dd>
      </div>
    </dl>
  </div>
</section>

<!-- Final CTA -->
<section class="bg-forest py-20 text-cream sm:py-24">
  <div class="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
    <h2 class="font-display text-3xl font-semibold text-cream sm:text-4xl">
      Ready to sell your saddle?
    </h2>
    <p class="mt-4 text-cream/75">
      List it free on Galopa — verified buyers, protected payments, and delivery pricing that's
      never a surprise.
    </p>
    <a
      routerLink="/account/listings/new"
      class="mt-8 inline-block rounded-full bg-cream px-8 py-3.5 text-sm font-medium text-forest transition hover:bg-tanlight"
    >
      List your saddle
    </a>
  </div>
</section>
